import { useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { debug as debugFn } from "debug";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { Promisable } from "type-fest";
import { v4 } from "uuid";

import type {
  ExtendedVisionUser,
  FormSectionCompleteProps,
  IncidentWorkItemId,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  IncidentWorkItemIdSchema,
  workflows,
} from "@thalamos/vision-common";

import api from "../../api/index.js";
import { useUser } from "../../Context/UserContext.js";
import { CreateIncidentWorkItemForm } from "../../forms/CreateIncidentWorkItemForm/CreateIncidentWorkItemForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ComponentError } from "../Error/Error.js";

const debug = debugFn("vision-frontend:CreateIncidentWorkItemPage");

export type CreateIncidentWorkItemProps = {
  /** How the page reports an organisation with no usable workflow version. */
  errorHandling: "throw" | "inline";
  user: ExtendedVisionUser;
};

export type CreateIncidentWorkItemInnerProps = CreateIncidentWorkItemProps & {
  onCancel: () => Promisable<void>;
  onCreated: (args: {
    incidentWorkItemId: IncidentWorkItemId;
    formSectionComplete: FormSectionCompleteProps;
  }) => Promisable<void>;
};

export type CreateIncidentWorkItemPageProps = {
  errorHandling: "throw" | "inline";
};

export function CreateIncidentWorkItem({
  errorHandling,
  user,
}: CreateIncidentWorkItemProps) {
  const navigate = useNavigate();

  return (
    <CreateIncidentWorkItemInner
      errorHandling={errorHandling}
      user={user}
      onCancel={() => {
        navigate(createRoute.home());
      }}
      onCreated={({ incidentWorkItemId, formSectionComplete }) => {
        navigate(createRoute.incidentSectionComplete(incidentWorkItemId), {
          state: formSectionComplete,
        });
      }}
    />
  );
}

export function CreateIncidentWorkItemInner({
  errorHandling,
  user,
  onCancel,
  onCreated,
}: CreateIncidentWorkItemInnerProps) {
  const id = useMemo(() => IncidentWorkItemIdSchema.parse(v4()), []);
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();

  const workflowVersion =
    user.sessionOrganisationConfiguration?.workflowVersion;

  const createIncidentSection =
    workflowVersion === undefined
      ? undefined
      : workflows[workflowVersion]?.createIncident.formDefinition.sections[0];

  if (workflowVersion === undefined || createIncidentSection === undefined) {
    const message =
      workflowVersion === undefined
        ? "Missing workflow version for the current organisation"
        : `Unknown workflow version ${workflowVersion} for the current organisation`;

    if (errorHandling === "throw") {
      throw new Error(message);
    }

    return <Box>{message}</Box>;
  }

  const formSchema = createIncidentSection.buildFormSpecification(
    {},
  ).formSchema;

  return (
    <>
      <CreateIncidentWorkItemForm
        onSubmit={async (values: Record<string, unknown>) => {
          try {
            const parsedValue = formSchema.parse(values);

            const response = await api.incidentWorkItem.create(
              id,
              {
                workflowVersion,
                createIncidentFormSection: parsedValue,
              },
              // make sure sending the correct fields for specific workflow
            );

            if (response.status === 204) {
              await onCreated({
                incidentWorkItemId: id,
                formSectionComplete: {
                  type: FormSectionSchema.enum.createIncidentMain,
                  payload: {
                    officer: user.name,
                  },
                },
              });
              return;
            } else {
              throw new Error(JSON.stringify(response.data.message));
            }
          } catch (error) {
            debug("Failed to create incident work item", error);
            toastNotifications.show(
              t("incidentWorkItem.notificationFailedToSave"),
              { severity: "error" },
            );
          }
        }}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        defaultCommandUnitOrganisationId={user.sessionContext?.organisationId!}
        onCancel={() => {
          void onCancel();
        }}
        workflowVersion={workflowVersion}
      />
    </>
  );
}

export function CreateIncidentWorkItemPage({
  errorHandling,
}: CreateIncidentWorkItemPageProps) {
  const { user, loading, error } = useUser();

  if (loading) {
    return <CircularProgress />;
  }

  if (error || user === undefined || user.sessionContext === null) {
    return <ComponentError />;
  }

  return (
    <CreateIncidentWorkItemPageInner
      errorHandling={errorHandling}
      user={user}
    />
  );
}

export function CreateIncidentWorkItemPageInner({
  user,
  errorHandling,
}: CreateIncidentWorkItemProps) {
  if (user.sessionContext === null) {
    return <ComponentError />;
  }

  return <CreateIncidentWorkItem errorHandling={errorHandling} user={user} />;
}
